import { CommonModule } from '@angular/common';
import { Component, effect, inject, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { $state, restManager } from 'app/ajs-upgraded-providers';
import { ToolbarEventsContextService } from 'src/app/core/services/toolbar-events/toolbar-events-context.service';
import { Allergens, Components, Items, Printers, Stock } from 'tilby-models';
import { ItemDetailsTabsComponent } from './item-details-tabs/item-details-tabs.component';
import { ItemGeneralComponent } from './item-details-tabs/item-general/item-general.component';
import { ItemChannelsComponent } from './item-details-tabs/item-channels/item-channels.component';
import { ItemInventoryComponent } from './item-details-tabs/item-inventory/item-inventory.component';
import { ItemStockComponent } from './item-details-tabs/item-stock/item-stock.component';
import { ItemPrintersComponent } from './item-details-tabs/item-printers/item-printers.component';
import { ItemDetailsSectionComponent } from './item-details-tabs/item-details-section/item-details-section.component';
import { ChildReceiverType, ItemCommunicationService } from 'src/app/features/items/components/items-module/items-tab/services/item-communication.service';
import { TranslateService } from '@ngx-translate/core';
import { ItemDataCatalogService } from 'src/app/features/items/services/item-data-catalog.service';
import { ItemGeneralFormService } from './item-details-tabs/item-general/item-general-form.service';
import { Subject, takeUntil } from 'rxjs';
import {
  ConfigurationManagerService,
  ConnectionService,
  EntityManagerService,
  LoaderService,
  ToolbarEventsService,
  UserActiveSessionManagerService
} from 'src/app/core';
import { AlertDialogService, ConfirmDialogService, GenericJsonDetailDialogService } from 'src/app/dialogs';
import { ItemOperationsService } from 'src/app/features/items/components/items-module/items-tab/services/item-operations.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ItemDetailsSectionFormService } from './item-details-tabs/item-details-section/item-details-section-form.service';
import { ImagesManagerService, ItemThumbnailType } from 'src/app/core/services/images-manager';
import { ItemVariationsSectionComponent } from './item-details-tabs/item-variations-section/item-variations-section.component';
import { ItemDefault } from 'src/app/features/items/models/items-form.model';
import { VariationsFormService } from './item-details-tabs/item-variations-section/variations/variations-form.service';
import { CombinationsFormService } from './item-details-tabs/item-variations-section/combinations/combinations-form.service';
import { ItemChannelsFormService } from './item-details-tabs/item-channels/item-channels-form.service';
import { ItemInventoryFormService } from './item-details-tabs/item-inventory/item-inventory-form.service';
import { ItemPrinterFormService } from './item-details-tabs/item-printers/item-printers-form.service';
import { ItemStockFormService } from './item-details-tabs/item-stock/item-stock-form.service';
import { ItemVariationsSectionService } from './item-details-tabs/item-variations-section/item-variations-section.service';

@Component({
  selector: 'app-item-details',
  standalone: true,
  imports: [
    CommonModule,
    ItemDetailsTabsComponent,
    ItemGeneralComponent, ItemChannelsComponent,
    ItemInventoryComponent, ItemDetailsSectionComponent,
    ItemVariationsSectionComponent, ItemStockComponent,
    ItemPrintersComponent, MatProgressSpinnerModule
  ],
  providers: [
    ItemOperationsService,
    ItemCommunicationService,
    ItemGeneralFormService,
    ItemDetailsSectionFormService,
    ItemVariationsSectionService,
    CombinationsFormService,
    ItemStockFormService,
    ItemInventoryFormService,
    ItemPrinterFormService,
    ItemChannelsFormService,
  ],
  templateUrl: './item-details.component.html',
  styleUrl: './item-details.component.scss'
})
export class ItemDetailsComponent implements OnInit, OnDestroy {
    
  private readonly _snackBar = inject(MatSnackBar);
  private readonly alertDialogService = inject(AlertDialogService);
  private readonly configurationManagerService = inject(ConfigurationManagerService);
  private readonly confirmDialogService = inject(ConfirmDialogService);  
  private readonly connection = inject(ConnectionService);
  private readonly entityManager = inject(EntityManagerService);
  private readonly genericJsonDetailDialog = inject(GenericJsonDetailDialogService);
  private readonly imagesManagerService = inject(ImagesManagerService);
  private readonly itemCommunicationService = inject(ItemCommunicationService);
  private readonly itemDataCatalogService = inject(ItemDataCatalogService);
  private readonly itemDetailsSectionFormService = inject(ItemDetailsSectionFormService);
  private readonly itemGeneralFormService = inject(ItemGeneralFormService);
  private readonly itemOperationsService = inject(ItemOperationsService);
  private readonly loaderService = inject(LoaderService);
  private readonly restManagerService = inject(restManager);
  private readonly state = inject($state);
  private readonly toolbarEventsContextService = inject(ToolbarEventsContextService);
  private readonly toolbarEventsService = inject(ToolbarEventsService);
  private readonly translate = inject(TranslateService);
  private readonly userActiveSessionManagerService = inject(UserActiveSessionManagerService);
  private readonly variationFormService = inject(VariationsFormService);
  
  protected readonly hasToolbarMenu = this.configurationManagerService.isUserPermittedOptIn("show_developer_options");
  private areEditsBlocked = this.configurationManagerService.isMagoEnabled() && this.userActiveSessionManagerService.getSession()?.role !== 'root';

  @Input() item!: Items;
  @Input() itemStock!: Stock;
  allergens: Allergens[] = [];
  orderPrinters: Printers[] = [];
  components: Components[] = [];
  itemLastMovemnets: any[] = [];

  private destroy$ = new Subject<void>(); 

  isNew: boolean = false;
  activeTabPanel: ChildReceiverType = 'general';

  constructor() {
    effect(() => {
      if(this.itemGeneralFormService.itemGeneralFormReady()) {
        this.listenToItemNameChanges();
      }
    })
  }

  ngOnInit(): void {
    if (!this.item) {
      this.item = {
        ...new ItemDefault(),
        on_sale: true,
        category: {id: 0} as any,
      } as Items;
    }
    this.isNew = !this.item?.id;

    if(!this.isNew) this.loadLastMovements();
    this.allergens = this.itemDataCatalogService.getDataByKey('allergens') || [];
    this.components = this.itemDataCatalogService.getDataByKey('components') || [];
    this.orderPrinters = this.itemDataCatalogService.getDataByKey('printers') || [];
    
    this.createToolbarButtons();
    this.initContextToolbarLabel();
    this.loaderService.forceHide();
  }

  private loadLastMovements() {
    const itemId = this.item.id;

    this.restManagerService.getList('stock_movements', {
      item_id: itemId, 
      per_page: 5, 
      orderby_desc: 'date', 
      price: 'notnull'
    }).then((res: any) => {
      if(res.results) this.itemDataCatalogService.addDataToCatalog('item-last-movements', res.results);
      this.itemGeneralFormService.itemDataGeneralFormReady.set(true);
    }).catch((err: any) => {
      // console.error("Errore nel recupero dei movimenti ", err)
    })
  }

  onTabPanelChange(textLabelPanel: string) {
    if(textLabelPanel === this.translate.instant("ITEMS.DETAILS.GENERAL"))
      this.activeTabPanel = 'general';
    else if(textLabelPanel === this.translate.instant("ITEMS.DETAILS.CHANNELS"))
      this.activeTabPanel = 'channels';
    else if(textLabelPanel === this.translate.instant("ITEMS.DETAILS.CODES"))
      this.activeTabPanel = 'inventory';
    else if(textLabelPanel === this.translate.instant("ITEMS.DETAILS.DETAILS"))
      this.activeTabPanel = 'details-tab';
    else if(textLabelPanel === this.translate.instant("ITEMS.DETAILS.VARIATIONS"))
      this.activeTabPanel = 'variations';
    else if(textLabelPanel === this.translate.instant("ITEMS.DETAILS.STOCK"))
      this.activeTabPanel = 'stock';
    else if(textLabelPanel === this.translate.instant("ITEMS.DETAILS.PRINTERS"))
      this.activeTabPanel = 'printers';

    // Notifica tutti i figli del cambio tab
    this.itemCommunicationService.createParentTrigger('tabActivated', this.activeTabPanel);
  }

  initContextToolbarLabel() {
    if(this.isNew) {
      this.toolbarEventsContextService.label = `ITEMS.TOPBAR_ITEMS.NAME_PLACEHOLDER`;
    } else {
      this.toolbarEventsContextService.label = (this.item.name || "" ) + '\u200b';
    }
  }

  private listenToItemNameChanges() {
    this.itemGeneralFormService.name?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: string) => {
      this.toolbarEventsContextService.label = value? value + '\u200b': `ITEMS.TOPBAR_ITEMS.NAME_PLACEHOLDER`;
    })
  }
  
  /**
   * Create buttons for the toolbar
   */
  createToolbarButtons = () => {
    this.toolbarEventsContextService.backButton$.next({
        isIt: signal(true),
        name: 'arrow_back',
        icon: signal('arrow_back'),
        click: () => this.back()
    });

    this.toolbarEventsContextService.buttons$.next({
        barButtons: [
            {
                isIt: () => true,
                name: 'save',
                icon: signal('check'),
                click: _ => {
                  this.save();
                }
            },
            {
                isIt: () => true,
                name: 'settings_backup_restore',
                icon: signal('settings_backup_restore'),
                click: _ => {
                  this.restore();
                }
            },
        ],
        panelButtons: []
    });

    this.toolbarEventsService.buttons$.next({
      barButtons: [
        {
          isIt: () => this.connection.isOffline(),
          name: 'cloud_off',
          icon: signal('cloud_off'),
          click: _ => null
        }
      ],
      panelButtons: [
        {
          isIt: signal(this.hasToolbarMenu),
          name: 'show_json_item',
          icon: signal('data_object'),
          label: signal('ITEMS.TOPBAR_ITEMS.SHOW_JSON'),
          click: _ => this.openItemDetailsJsonDetail()
        }
      ]
    })
  }

  async openItemDetailsJsonDetail() {
    await this.genericJsonDetailDialog.openDialog(
      { data: 
        { title:'ITEMS.DETAILS.TITLE_OPEN_JSON_DETAIL', 
          nameFile: 'item_json_data', 
          json: this.itemOperationsService.buildCurrentItem() } 
      });
  }

  back() {
    this.state.go(`app.new.items`);
    this.toolbarEventsContextService.buttons$.next({panelButtons: [], barButtons: []});
  }

  async restore() {
    let confirm = await this.confirmDialogService.openDialog({
      data: {
        messageLabel: 'ITEMS.DETAILS.UNDO_CHANGES',
      }
    });

    if(!confirm) return;
                  
    this.itemCommunicationService.createParentTrigger('cancel', 'all');
  }

  async save() {
    if(this.areEditsBlocked) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('ITEMS.NO_EDIT_WITH_MAGO_ENABLED')
        }})
      return;
    }

    let invalidFields = this.itemOperationsService.checkItemValidity();

    if(invalidFields?.length) {
      const invalidSection = [...new Set(invalidFields.map(error => error.split(">")[0].trim()))].join(', ');
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('FORM_ERRORS.THERE_ARE_ERRORS', {value: invalidSection})
        }})
      return;
    }

    if (this.itemOperationsService.checkEmptyBomComponents()) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: this.translate.instant('ITEMS.DETAILS.BOM_COMPONENT_WITHOUT_SUB_BOM_COMPONENTS')
        }})
      return;
    }

    let confirm = await this.confirmDialogService.openDialog({
      data: {
        messageLabel: 'ITEMS.DETAILS.WANT_TO_SAVE',
      }
    });

    if(!confirm) return;

    this.loaderService.show();

    try {
      // GESTIONE DELLE IMMAGINI PRINCIPALI

      // Recupero l'array delle immagini principali
      const imagesList = this.itemDetailsSectionFormService.images?.value || [];
      
      // Recupero l'URL della Thumbnail selezionata (potrebbe essere un blob locale)
      const currentThumbnailVal = this.itemDetailsSectionFormService.thumbnail?.value;

      await this.imagesManagerService.saveAllImages(imagesList);

      const finalThumbnailUrl = imagesList.find((img: ItemThumbnailType) => 
        img.image_url === currentThumbnailVal ||
        (img as any)._originalBlob === currentThumbnailVal
      ) || null;

      this.itemDetailsSectionFormService.thumbnail?.setValue(finalThumbnailUrl?.image_url);

      // GESTIONE IMMAGINI DELLE VARIANTI

      const variationsControls = this.variationFormService.variationsArray?.controls || [];
      const variationUploadPromises: Promise<any>[] = [];

      for (const variationGroup of variationsControls) {
        const variationValuesArray = variationGroup.get('variation_values') as any;
        
        if (variationValuesArray && variationValuesArray.controls) {
          for (const valGroup of variationValuesArray.controls) {
            const imageControl = valGroup.get('_image');
            const imgData = imageControl?.value;

            if (imgData) {
              const uploadPromise = this.imagesManagerService.saveAllImages([imgData]).then(() => {
                valGroup.get('image_url')?.setValue(imgData.image_url);
              });
              variationUploadPromises.push(uploadPromise);
            }
          }
        }
      }

      // Attendiamo che tutti gli upload delle varianti siano completati
      if (variationUploadPromises.length > 0) {
        await Promise.all(variationUploadPromises);
      }

    } catch(error) {
      this.alertDialogService.openDialog({
        data: {
          messageLabel: 'ITEMS.DETAILS.IMAGES_SAVE_HAS_ERRORS'
        }
      });
    }
    
    const itemToSend = {
      ...this.itemOperationsService.buildCurrentItem(),
      ...(this.item?.id && {id: this.item.id})
    };

    try {
      if(itemToSend.id) {
        this.item = await this.entityManager.items.putOneOnline(itemToSend);
      } else {
        this.item = await this.entityManager.items.postOneOnline(itemToSend);
      }

      // Aggiornamento lista prodotti
      // const currentItems = this.itemDataCatalogService.getDataByKey('items') || [];

      // if (itemToSend.id) {
      //   const index = currentItems.findIndex(i => i.id === this.item.id);
      //   if (index !== -1) {
      //     currentItems[index] = this.item;
      //   }
      // } else {
      //   currentItems.push(this.item);
      // }

      // this.itemDataCatalogService.addDataToCatalog('items', currentItems);

      this.state.go(`app.new.items.showcase`);

      this._snackBar.open(this.translate.instant('ITEMS.DETAILS.SAVED'), undefined, { 
        duration: 3000, 
        horizontalPosition: 'start', 
        verticalPosition: "bottom", 
      });
    } catch (e: any) {
      const isDuplicateSku = e?.data?.error?.message?.includes('Sku already present');
      
      let messageLabel = '';
      if (isDuplicateSku) {
        messageLabel = 'ITEMS.DETAILS.ITEM_SAVE_DUPLICATE_SKU';
      } else {
        messageLabel = itemToSend.id ? 'ITEMS.DETAILS.ERROR_IN_EDIT' : 'ITEMS.DETAILS.ERROR_IN_CREATION';
      }

      this.alertDialogService.openDialog({
        data: {
          messageLabel: messageLabel
        }
      });
    } finally {
      this.loaderService.hide();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.itemDataCatalogService.addDataToCatalog('item-last-movements', []);
  }

}
